// taxes.reducer.ts
import { setAdsLabelEnabledSuccess } from '@luggagehero/dashboard-data-access-business-profiles';
import { ManageableStorageLocation } from '@luggagehero/shared/interfaces';
import { createReducer, on } from '@ngrx/store';

import * as Actions from './storage-locations.actions';
import { initialState, SaveStatus, StorageLocationsState } from './storage-locations.model';

export const storageLocationsKey = 'storageLocationsKey';
/**
 * AM-4724: a shop can live in the organization's list, in the by-id cache, or both. Splitting the
 * two stores means every mutation has to reach wherever the shop ACTUALLY lives — patching only the
 * list silently drops the write for a shop opened straight from the Business Profiles estate view,
 * because those live only in the cache. That is how the first cut of this change regressed the
 * AM-4655 ads toggle: it wrote to the list, found nothing, and the toggle snapped back.
 */
function writeThrough(
  state: StorageLocationsState,
  id: string,
  update: (existing: ManageableStorageLocation) => ManageableStorageLocation,
): Pick<StorageLocationsState, 'storageLocations' | 'storageLocationsById'> | null {
  const index = state.storageLocations.findIndex((location) => location._id === id);

  // Nowhere to write and nothing to seed: report "no change" so the caller can hand back the SAME
  // state reference. Returning a fresh object for a no-op re-runs every selector and defeats OnPush
  // — an existing AM-4655 test pins that with a reference-equality assertion.
  if (index === -1 && state.storageLocationsById[id] == null) {
    return null;
  }

  const storageLocations =
    index === -1
      ? state.storageLocations
      : state.storageLocations.map((location, i) => (i === index ? update(location) : location));

  // Only ever UPDATES what is already held. Seeding a shop that is in neither store would let a
  // late response — a save that resolved after the organization was switched — put another
  // organization's shop back into a freshly-reset cache.
  const cached = state.storageLocationsById[id];
  const storageLocationsById = cached
    ? { ...state.storageLocationsById, [id]: update(cached) }
    : state.storageLocationsById;

  return { storageLocations, storageLocationsById };
}

export const storageLocationsReducer = createReducer(
  initialState,
  on(
    Actions.createHotelsHeroStorageLocation,
    (state): StorageLocationsState => ({ ...state, isCreating: true, error: null }),
  ),
  on(
    Actions.createHotelsHeroStorageLocationSuccess,
    (state): StorageLocationsState => ({ ...state, isCreating: false }),
  ),
  on(Actions.createHotelsHeroStorageLocationFailure, (state, action): StorageLocationsState => {
    const error = action.error;
    if (
      typeof error === 'object' &&
      error != null &&
      'error' in error &&
      typeof error.error === 'object' &&
      error.error != null &&
      'message' in error.error &&
      typeof error.error.message === 'string'
    ) {
      return {
        ...state,
        error: error.error.message,
        isCreating: false,
      };
    }
    return {
      ...state,
      error: 'An error occurred',
      isCreating: false,
    };
  }),
  on(Actions.loadStorageLocations, (state): StorageLocationsState => ({ ...state, isLoadingList: true, error: null })),
  on(
    Actions.loadStorageLocationsSuccess,
    (state, action): StorageLocationsState => ({
      ...state,
      isLoadingList: false,
      hasLoadedListOnce: true,
      loadedForQuery: action.searchQuery,
      storageLocations: action.storageLocations,
    }),
  ),
  on(
    Actions.loadStorageLocationsFailure,
    (state, _action): StorageLocationsState => ({
      ...state,
      error: 'An error occurred',
      isLoadingList: false,
    }),
  ),
  on(Actions.createStorageLocation, (state): StorageLocationsState => ({ ...state, isCreating: true, error: null })),
  on(
    Actions.createStorageLocationSuccess,
    (state, action): StorageLocationsState => ({
      ...state,
      isCreating: false,
      // AM-4752: replace by _id rather than append. A create returns a new shop today, so this is
      // defensive: a shop that is somehow already listed must never be listed twice.
      storageLocations: [
        ...state.storageLocations.filter((existing) => existing._id !== action.storageLocation._id),
        action.storageLocation,
      ],
    }),
  ),
  // AM-4752: a detected duplicate ends the create just as success and failure do. Without this the
  // spinner never stops, because `isCreating` is only ever cleared by the other two.
  on(
    Actions.createStorageLocationDuplicate,
    (state): StorageLocationsState => ({ ...state, isCreating: false, error: null }),
  ),
  on(Actions.createStorageLocationFailure, (state, action): StorageLocationsState => {
    const error = action.error;
    if (
      typeof error === 'object' &&
      error != null &&
      'error' in error &&
      typeof error.error === 'object' &&
      error.error != null &&
      'message' in error.error &&
      typeof error.error.message === 'string'
    ) {
      return {
        ...state,
        error: error.error.message,
        isCreating: false,
      };
    }
    return {
      ...state,
      error: 'An error occurred',
      isCreating: false,
    };
  }),
  on(
    Actions.setSelectedStorageLocations,
    (state, action): StorageLocationsState => ({
      ...state,
      selectedStorageLocations: action.storageLocations,
    }),
  ),
  on(
    Actions.setSelectedStorageLocationById,
    (state, action): StorageLocationsState => ({
      ...state,
      selectedStorageLocations: [action.id],
    }),
  ),
  on(
    Actions.resetSelectedStorageLocations,
    (state): StorageLocationsState => ({
      ...state,
      selectedStorageLocations: [],
    }),
  ),
  on(
    Actions.loadStorageLocationById,
    (state): StorageLocationsState => ({ ...state, isLoadingById: true, error: null }),
  ),
  on(Actions.loadStorageLocationByIdSuccess, (state, action): StorageLocationsState => {
    // AM-4724: refresh a location the organization's list already holds, but never APPEND to that
    // list. The Business Profiles estate view links to any shop in the estate, so appending meant a
    // shop from another organization landed on this organization's Locations page and stayed there.
    // Individually-fetched shops go in the by-id cache instead, which is what
    // `selectSelectedStorageLocation` falls back to.
    const existingLocationIndex = state.storageLocations.findIndex(
      (location) => location._id === action.storageLocation._id,
    );
    const storageLocations =
      existingLocationIndex === -1
        ? state.storageLocations
        : state.storageLocations.map((location, index) =>
            index === existingLocationIndex ? action.storageLocation : location,
          );

    return {
      ...state,
      isLoadingById: false,
      storageLocations,
      storageLocationsById: {
        ...state.storageLocationsById,
        [action.storageLocation._id]: action.storageLocation,
      },
    };
  }),
  on(Actions.loadStorageLocationByIdFailure, (state, action): StorageLocationsState => {
    const error = action.error;
    if (
      typeof error === 'object' &&
      error != null &&
      'error' in error &&
      typeof error.error === 'object' &&
      error.error != null &&
      'message' in error.error &&
      typeof error.error.message === 'string'
    ) {
      return {
        ...state,
        error: error.error.message,
        isLoadingById: false,
        isLoadingList: false,
      };
    }
    return {
      ...state,
      error: 'An error occurred',
      isLoadingById: false,
      isLoadingList: false,
    };
  }),
  on(
    Actions.setSearchQuery,
    (state, action): StorageLocationsState => ({
      ...state,
      searchQuery: action.searchQuery,
      hasSearchedBefore: state.hasSearchedBefore || (action.searchQuery != null && action.searchQuery.trim() !== ''),
    }),
  ),
  on(
    Actions.updateStorageLocation,
    (state, { storageLocationId }): StorageLocationsState => ({
      ...state,
      isUpdating: true,
      error: null,
      saveStatusById: { ...state.saveStatusById, [storageLocationId]: 'saving' as SaveStatus },
    }),
  ),
  on(Actions.updateStorageLocationSuccess, (state, { storageLocationId, storageLocation }) => ({
    ...state,
    isUpdating: false,
    // A successful save must reach the cache too, or a shop opened from the estate view reports
    // success while every detail tab keeps rendering the pre-save values.
    ...writeThrough(state, storageLocationId, () => storageLocation),
    saveStatusById: { ...state.saveStatusById, [storageLocationId]: 'success' as SaveStatus },
  })),

  on(Actions.updateStorageLocationFailure, (state, { storageLocationId, error }) => ({
    ...state,
    saveStatusById: { ...state.saveStatusById, [storageLocationId]: 'error' as SaveStatus },
    saveErrorById: { ...state.saveErrorById, [storageLocationId]: error },
  })),
  on(
    Actions.loadStorageLocationPendingTasksSuccess,
    (state: StorageLocationsState, { storageLocationId, pendingTasks, activation }): StorageLocationsState => {
      return {
        ...state,
        storageLocationsPendingTasks: {
          ...state.storageLocationsPendingTasks,
          [storageLocationId]: pendingTasks,
        },
        activationStatusByStorageLocationId: {
          ...state.activationStatusByStorageLocationId,
          [storageLocationId]: activation,
        },
      };
    },
  ),
  on(
    Actions.requestStorageLocationReview,
    (state, { storageLocationId }): StorageLocationsState => ({
      ...state,
      reviewRequestStatusById: { ...state.reviewRequestStatusById, [storageLocationId]: 'saving' as SaveStatus },
    }),
  ),
  // The publish endpoint answers with the same checklist payload as the tasks endpoint, so the
  // reply is authoritative for both the rows and the handshake — no follow-up reload needed.
  on(
    Actions.requestStorageLocationReviewSuccess,
    (state, { storageLocationId, pendingTasks, activation }): StorageLocationsState => ({
      ...state,
      storageLocationsPendingTasks: {
        ...state.storageLocationsPendingTasks,
        [storageLocationId]: pendingTasks,
      },
      activationStatusByStorageLocationId: {
        ...state.activationStatusByStorageLocationId,
        [storageLocationId]: activation,
      },
      reviewRequestStatusById: { ...state.reviewRequestStatusById, [storageLocationId]: 'success' as SaveStatus },
    }),
  ),
  on(
    Actions.requestStorageLocationReviewFailure,
    (state, { storageLocationId }): StorageLocationsState => ({
      ...state,
      reviewRequestStatusById: { ...state.reviewRequestStatusById, [storageLocationId]: 'error' as SaveStatus },
    }),
  ),
  // AM-4655: the ads-label toggle is dispatched from the business-profiles store, but the flag it
  // sets lives on the shop — so the shop held here has to reflect it, or the toggle snaps back to
  // the stale value on the next change detection.
  on(setAdsLabelEnabledSuccess, (state, { result }): StorageLocationsState => {
    const written = writeThrough(state, result.shopId, (existing) => ({
      ...existing,
      adsLabelEnabled: result.adsLabelEnabled,
    }));

    return written ? { ...state, ...written } : state;
  }),
  on(Actions.updateQrCodePrintedStatusSuccess, (state, { storageLocationId, printed }) => {
    // Two things move: the gate row (printing satisfies signage-or-app) and the flag on the shop
    // itself, which the "did you print?" prompt is keyed on. The flag goes through writeThrough —
    // a shop opened from the Business Profiles estate view lives ONLY in the by-id cache, so
    // patching the list alone would drop the write and the prompt would reappear on every visit,
    // which is the regression this change exists to fix.
    const written = writeThrough(state, storageLocationId, (location) => ({
      ...location,
      isQRCodePrinted: printed,
    }));

    const pendingTasks = state.storageLocationsPendingTasks[storageLocationId];
    if (!pendingTasks) {
      // No task rows to touch; hand back the SAME reference when nothing was written either.
      return written ? { ...state, ...written } : state;
    }

    const updatedPendingTasks = pendingTasks.map((task) =>
      // Matches the legacy key too: under deploy skew the api still emits 'print_signage', and the
      // row would otherwise not flip locally after the partner confirms printing.
      task.key === 'signage_or_app' || task.key === 'print_signage' ? { ...task, completed: printed } : task,
    );

    return {
      ...state,
      ...(written ?? {}),
      storageLocationsPendingTasks: {
        ...state.storageLocationsPendingTasks,
        [storageLocationId]: updatedPendingTasks,
      },
    };
  }),
  on(
    Actions.loadMembersSuccess,
    (state, { storageLocationId, members }): StorageLocationsState => ({
      ...state,
      membersByStorageLocationId: { ...state.membersByStorageLocationId, [storageLocationId]: members },
    }),
  ),
  on(
    Actions.loadPendingInvitationsSuccess,
    (state, { storageLocationId, invitations }): StorageLocationsState => ({
      ...state,
      pendingInvitationsByStorageLocationId: {
        ...state.pendingInvitationsByStorageLocationId,
        [storageLocationId]: invitations,
      },
    }),
  ),
  on(
    Actions.cancelInvitationSuccess,
    (state, { storageLocationId, token }): StorageLocationsState => ({
      ...state,
      pendingInvitationsByStorageLocationId: {
        ...state.pendingInvitationsByStorageLocationId,
        [storageLocationId]: (state.pendingInvitationsByStorageLocationId[storageLocationId] ?? []).filter(
          (inv) => inv.token !== token,
        ),
      },
    }),
  ),
  on(Actions.reset, (): StorageLocationsState => initialState),
);
